import { useState, useEffect } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { toast } from "sonner";

/**
 * Reusable Bulk Upload Hook
 * Extracts common logic from Attendance, Health, Activities, and Results uploads
 *
 * CUSTOMIZATION POINTS:
 * - Each module can override any behavior through callbacks
 * - Custom validation, transformation, and success handlers supported
 * - Module-specific features won't affect other modules
 *
 * @param {Object} config - Configuration object
 * @param {string} config.templateEndpoint - API endpoint for template download
 * @param {string} config.previewEndpoint - API endpoint for upload preview
 * @param {string} config.confirmEndpoint - API endpoint for upload confirmation
 * @param {string} config.successMessage - Success message prefix (e.g., "attendance records")
 * @param {boolean} config.useWeekFilter - Whether to use week start/end date filters (default: true)
 * @param {Object} config.customFilters - Additional custom filters specific to the component
 * @param {Function} config.onUploadSuccess - Callback after successful upload
 * @param {Function} config.onDownloadSuccess - Callback after successful template download
 * @param {Function} config.customFileValidation - Custom file validation logic (optional)
 * @param {Function} config.transformPreviewData - Transform preview data before storing (optional)
 * @param {Function} config.buildCustomParams - Custom parameter builder for template download (optional)
 * @param {string} config.downloadFileNamePattern - Custom filename pattern (default: template_{date}.xlsx)
 * @param {boolean} config.useEnhancedPreview - Use enhanced preview with old→new comparison (default: false)
 */
const useBulkUpload = (config = {}) => {
  const {
    templateEndpoint,
    previewEndpoint,
    confirmEndpoint,
    successMessage = "records",
    useWeekFilter = true,
    customFilters = {},
    onUploadSuccess,
    onDownloadSuccess,
    customFileValidation,
    transformPreviewData,
    buildCustomParams,
    downloadFileNamePattern,
    useEnhancedPreview,
  } = config;

  const [isEnhancedPreview, setIsEnhancedPreview] = useState(
    config.useEnhancedPreview || false
  );

  // Filter state
  const [filters, setFilters] = useState({
    weekStart: "",
    weekEnd: "",
    classFilter: "",
    wardenFilter: "",
    ...customFilters,
  });

  // Data state
  const [classes, setClasses] = useState([]);
  const [wardens, setWardens] = useState([]);

  // Upload state
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Initialize week dates if useWeekFilter is true
  useEffect(() => {
    if (useWeekFilter) {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      setFilters((prev) => ({
        ...prev,
        weekStart: monday.toISOString().split("T")[0],
        weekEnd: sunday.toISOString().split("T")[0],
      }));
    }

    fetchFiltersData();
  }, [useWeekFilter]);

  /**
   * Fetch classes and wardens for filters
   */
  const fetchFiltersData = async () => {
    try {
      const userData = JSON.parse(
        localStorage.getItem("hostelconnect_user") || "{}"
      );
      const hostelId = userData.hostel_id;

      if (!hostelId) {
        warn("No hostel_id found");
        return;
      }

      // Fetch classes
      const classesRes = await apiClient.get(
        `/v1/admin/classes/?hostel_id=${hostelId}`
      );
      setClasses(classesRes.data || []);

      // Fetch wardens
      const wardensRes = await apiClient.get(
        `/v1/admin/wardens/?hostel_id=${hostelId}`
      );
      setWardens(wardensRes.data || []);
    } catch (error) {
      toast.error("Failed to load filter options");
    }
  };

  /**
   * Update a specific filter value
   */
  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Download template with current filters
   * Supports custom parameter builder for module-specific needs
   */
  const handleDownloadTemplate = async (customParams = {}) => {
    if (useWeekFilter && (!filters.weekStart || !filters.weekEnd)) {
      toast.error("Please select week range");
      return;
    }

    setDownloading(true);
    try {
      const params = new URLSearchParams();

      // Use custom parameter builder if provided, otherwise use default logic
      if (buildCustomParams) {
        const customParamsObj = buildCustomParams(filters, customParams);
        Object.entries(customParamsObj).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
      } else {
        // Default parameter building
        if (useWeekFilter) {
          params.append("week_start", filters.weekStart);
          params.append("week_end", filters.weekEnd);
        }

        if (filters.classFilter) {
          params.append("class_filter", filters.classFilter);
        }
        if (filters.wardenFilter) {
          params.append("warden_filter", filters.wardenFilter);
        }

        // Add any additional custom parameters
        Object.entries(customParams).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
      }

      const response = await apiClient.get(
        `${templateEndpoint}?${params.toString()}`,
        {
          responseType: "blob",
        }
      );

      // Generate filename (custom pattern or default)
      const filename = downloadFileNamePattern
        ? typeof downloadFileNamePattern === "function"
          ? downloadFileNamePattern(filters)
          : downloadFileNamePattern
        : useWeekFilter
          ? `template_${filters.weekStart}_to_${filters.weekEnd}.xlsx`
          : `template_${new Date().toISOString().split("T")[0]}.xlsx`;

      const mimeType = response.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      await downloadFile(response.data, filename, mimeType);

      if (onDownloadSuccess) {
        onDownloadSuccess(response, filters);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to download template"
      );
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Validate uploaded file
   * Uses custom validation if provided, otherwise uses default
   */
  const validateFile = (file) => {
    // Use custom validation if provided
    if (customFileValidation) {
      return customFileValidation(file);
    }

    // Default validation
    if (!file) {
      return { valid: false, error: "No file selected" };
    }

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      return {
        valid: false,
        error: "Please upload an Excel file (.xlsx or .xls)",
      };
    }

    return { valid: true };
  };

  /**
   * Handle file upload with preview
   * Supports custom form data and preview data transformation
   */
  const handleFileUpload = async (e, customFormData = {}) => {
    const file = e.target.files[0];

    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      Object.entries(customFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await apiClient.post(previewEndpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const previewResult = transformPreviewData
        ? transformPreviewData(response.data.results)
        : response.data.results;

      console.log("previewResult", previewResult);

      // Update state for the UI (Modals)
      setPreviewData(previewResult);
      if (previewResult?.new_records) {
        setIsEnhancedPreview(true);
      }
      // Check if duplicates exist
      if (previewResult.duplicate_count > 0) {
        setShowDuplicateModal(true);
        setUploading(false);
      } else {
        // FIX: Pass the fresh previewResult directly to avoid async state issues
        await confirmUpload("skip", previewResult);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
      setUploading(false);
    } finally {
      e.target.value = "";
    }
  };

  /**
   * Confirm upload with chosen action (skip or overwrite duplicates)
   */
  /**
   * Confirm upload with chosen action (skip or overwrite duplicates)
   * @param {string} action - 'skip' or 'overwrite'
   * @param {Object} overrideData - Optional direct data pass to bypass async state issues
   */
  const confirmUpload = async (action, overrideData = null) => {
    // Use the directly passed data if available, otherwise fallback to state
    const dataToProcess = overrideData || previewData;

    if (!dataToProcess) {
      toast.error("No data available to process.");
      setUploading(false);
      return;
    }

    try {
      const payload = {
        records: dataToProcess.new_records || [],
        duplicates: dataToProcess.duplicates || [],
        action: action,
      };

      const response = await apiClient.post(confirmEndpoint, payload);

      toast.success(
        `✅ Successfully processed ${response.data.successful} ${successMessage}!`
      );

      if (response.data.skipped > 0) {
        toast.info(`ℹ️ Skipped ${response.data.skipped} duplicate records`);
      }

      if (response.data.overwritten > 0) {
        toast.info(`🔄 Updated ${response.data.overwritten} existing records`);
      }

      if (response.data.notified_parents > 0) {
        toast.success(`📧 Notified ${response.data.notified_parents} parents`);
      }

      setShowDuplicateModal(false);
      setPreviewData(null);

      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload confirmation failed");
    } finally {
      setUploading(false);
    }
  };

  /**
   * Close duplicate modal
   */
  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setUploading(false);
  };

  /**
   * Get today's date in YYYY-MM-DD format
   * Useful for date input max validation
   */
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  /**
   * Set all filters at once (useful for custom filter management)
   */
  const setAllFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  return {
    // State
    filters,
    classes,
    wardens,
    downloading,
    uploading,
    previewData,
    showDuplicateModal,

    // Actions
    updateFilter,
    setAllFilters,
    handleDownloadTemplate,
    handleFileUpload,
    confirmUpload,
    closeDuplicateModal,
    fetchFiltersData,

    // Utility functions
    getTodayDate,
    validateFile,

    // Config
    useEnhancedPreview: isEnhancedPreview,
  };
};

export default useBulkUpload;
